import { DashboardState, ESdChangeReason, IApiManagement, IApiManagementDto } from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import * as React from "react";
import { connect } from "react-redux";
import { ApiManagementService } from "../../../services/api-management-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { getSdInstance } from "../../../shared/utility/designer-context";
import { openApiForEditing } from "../../../shared/utility/designer-view-actions";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction } from "../../../store/unsaved-changes-modal/actions";
import { DeleteModal } from "../../common/delete-modal";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import { ApiBuilderLanding } from "./api-builder-landing";
import { ApiEditor } from "./api-editor";
import { buildApiRowActions } from "./api-row-actions";

/**
 * Host for the API Builder: it decides between the landing list and one API's editor, and
 * owns the API-level mutations.
 *
 * It used to be the editor itself — a hand-built header, an inline rename staged in
 * component state, an UPDATE button that both saved the name and closed the API, an
 * object-hash dirty check and a `CloseConfirmationModal` to guard it. The rename saves
 * when it is submitted now, so there is nothing unsaved left to confirm and the whole
 * dirty apparatus is gone.
 *
 * What is NOT gone is the callback replay: `shortcut-listener-for-navigation.tsx` defers
 * every navigation shortcut behind `sd.getSelectedApi()`, and this component is the only
 * thing that runs the callback it parks in the store. Without it, keyboard navigation away
 * from an open API silently does nothing.
 */

export interface IApiManagementCanvasProps {
  nextCallback?: () => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

interface IApiManagementCanvasState {
  isCreating: boolean;
  /** Where the name sheet's create stands. Owned by `SaveStatus`, never set directly. */
  createStatus: KuikaButtonStatus;
  busyApiId?: string;
  apiToDelete?: IApiManagement;
}

class ApiManagement extends PureSdComponent<IApiManagementCanvasProps & IWithSDProps, IApiManagementCanvasState> {
  /**
   * The create's state machine, shown on the name sheet's confirm. A create opens the new API's
   * editor, which replaces the list and the sheet with it — so the ✓ is held first and the editor
   * opens after it, and `canStart` keeps an Enter pressed during the ✓ from creating a second API.
   */
  private readonly createStatus: SaveStatus = new SaveStatus((createStatus: KuikaButtonStatus): void => {
    this.setState({ createStatus });
  });

  constructor(props: IApiManagementCanvasProps & IWithSDProps) {
    super(props);
    this.state = {
      isCreating: false,
      createStatus: "idle",
      busyApiId: undefined,
      apiToDelete: undefined
    };
  }

  /** The project whose APIs have been fetched, so the fetch runs once per project. */
  private loadedProjectId?: string;

  componentDidMount = (): void => {
    this.ensureApisLoaded();
  };

  componentDidUpdate(): void {
    this.ensureApisLoaded();
    // A pending cross-tool navigation closes the editor first, then runs.
    if (this.props.nextCallback && this.getOpenApi()) {
      this.closeEditor();
    }
  }

  /**
   * `loadApiManagements` returns early on an undefined project id, so a mount that lands
   * before the project does would leave the list permanently empty — this tool is the only
   * reader of `sd.apiManagements`, and nothing else ever fetches it. Retrying on update
   * also covers the project changing underneath (a branch switch).
   */
  ensureApisLoaded = (): void => {
    const projectId: string | undefined = this.props.sd?.project?.id;
    if (!projectId || this.loadedProjectId === projectId) return;
    this.loadedProjectId = projectId;
    this.props.sd.loadApiManagements(projectId);
  };

  /**
   * Nothing else replays a parked navigation callback, so one left behind when this host
   * goes away would fire on a LATER visit — a redirect the user asked for minutes ago.
   * Leaving the tool by any route other than the handshake drops it instead.
   */
  componentWillUnmount(): void {
    this.createStatus.dispose();
    if (this.props.nextCallback) {
      this.props.setNextCallbackFunction();
    }
  }

  getOpenApi = (): IApiManagement | undefined => this.props.sd.getSelectedApi();

  getApis = (): IApiManagement[] => this.props.sd?.apiManagements ?? [];

  //  ── Mutations ──────────────────────────────────────────────────────────────

  handleCreate = async (name: string): Promise<void> => {
    if (!this.createStatus.canStart) return;
    this.createStatus.start();
    this.setState({ isCreating: true });
    try {
      const dto = {
        id: Guid.create().toString(),
        name,
        kProject_Id: this.props.sd.project?.id,
        json: {},
        xml: "api"
      } as IApiManagementDto;
      await this.props.sd.saveApiManagements(dto);
      // The provider's own reload is not awaited, and the editor can only open an API the list
      // already holds — the sheet would otherwise hand over to a page still showing the list.
      await this.props.sd.loadApiManagements(this.props.sd.project?.id);
      // Creating an API opens it: there is nothing else to do to it from the list, and
      // the next thing anyone does is add a method. It opens once the ✓ has been seen.
      this.createStatus.succeed((): void => this.props.sd.setActiveApi(dto.id));
    } catch (error) {
      this.createStatus.fail();
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isCreating: false });
    }
  };

  /**
   * The panel's delete sequence, kept verbatim — service, then prune the StartProcess
   * reverse index, then reload, then fall back to the list if the deleted API was open.
   * The provider's own `deleteApi` is NOT used: it re-saves the API it has just deleted.
   */
  handleDelete = async (api: IApiManagement): Promise<void> => {
    this.setState({ busyApiId: api.id });
    try {
      const response = await ApiManagementService.Delete(api.id);
      if (response) {
        getSdInstance().startProcessTriggerSync.pruneApiManagement(api.id);
        this.props.sd.loadApiManagements(this.props.sd?.project?.id);
        if (this.props.sd.selectedApiId === api.id) {
          this.props.sd.setActiveApi(undefined!);
          this.props.sd.setEditingMethod(undefined);
        }
        KMainFunctions.displaySuccessNotification("Api deleted successfully");
      }
    } catch (error) {
      KMainFunctions.displayErrorNotification("An error occurred!", error);
    }
    this.setState({ busyApiId: undefined, apiToDelete: undefined });
  };

  //  ── Navigation ─────────────────────────────────────────────────────────────

  handleOpenApi = (api: IApiManagement): void => {
    void openApiForEditing(getSdInstance(), api.id, { ensureView: false });
  };

  /** Leaving the editor, and the one place a parked navigation callback is replayed. */
  closeEditor = (): void => {
    const next: (() => void) | undefined = this.props.nextCallback;
    this.props.sd.cancelEditingApi();
    this.props.sd.cancelActiveMethod();
    if (next) {
      next();
      this.props.setNextCallbackFunction();
    }
  };

  render() {
    if (!this.props.sd || this.props.sd.dashboardState !== DashboardState.apiManagement) {
      return null;
    }

    const openApi: IApiManagement | undefined = this.getOpenApi();

    return (
      <>
        <KuikaSpinnerSmall spinning={this.state.isCreating && !openApi}>
          {openApi ? (
            <ApiEditor
              key={openApi.id}
              onBack={this.closeEditor}
              onSwitchApi={this.handleOpenApi}
              onCreateNew={this.closeEditor}
              rowActions={buildApiRowActions((api: IApiManagement) => this.setState({ apiToDelete: api }))}
            />
          ) : (
            <ApiBuilderLanding
              isCreating={this.state.isCreating}
              createStatus={this.state.createStatus}
              busyApiId={this.state.busyApiId}
              onCreate={this.handleCreate}
              onOpenApi={this.handleOpenApi}
              onRequestDelete={(api: IApiManagement) => this.setState({ apiToDelete: api })}
            />
          )}
        </KuikaSpinnerSmall>

        {/* Owned by the host so the confirmation survives the surface it was opened from —
            the editor closes itself when the open API is the one deleted. */}
        <DeleteModal
          itemName={this.state.apiToDelete?.name ?? ""}
          triggerModal={!!this.state.apiToDelete}
          deleteItem={() => {
            const target: IApiManagement | undefined = this.state.apiToDelete;
            if (target) void this.handleDelete(target);
          }}
          deleteType={"API"}
          closeModal={() => this.setState({ apiToDelete: undefined })}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const mapStateToProps = (state: AppState) => ({
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const apiManagementCanvas = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<IApiManagementCanvasProps>(ApiManagement));
export { apiManagementCanvas as ApiManagementCanvas };
